import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { articles, type Article } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gray-100 rounded-full">
              <i className="ri-error-warning-line text-2xl text-gray-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              文章未找到
            </h1>
            <p className="text-gray-500 mb-6">该文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-400 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter(
      (a) => a.category === article.category && a.slug !== article.slug
    )
    .slice(0, 3);

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.description,
        image: article.imageUrl,
        datePublished: article.date,
        author: {
          "@type": "Person",
          name: article.author,
        },
        publisher: {
          "@type": "Organization",
          name: "芒果视频",
          logo: {
            "@type": "ImageObject",
            url: "https://mangguoshipin.com/logo.png",
          },
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://mangguoshipin.com/article/${article.slug}`,
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://mangguoshipin.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: category?.name || "分类",
            item: `https://mangguoshipin.com/category/${article.category}`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: article.title,
          },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${article.title} - 芒果视频`}
        description={article.description}
        keywords={article.tags.join(",")}
        canonicalUrl={`https://mangguoshipin.com/article/${article.slug}`}
        ogImage={article.imageUrl}
        ogType="article"
        schemaData={schemaData}
      />
      <Header />
      <main className="pt-16">
        <article>
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                {
                  label: category?.name || "分类",
                  href: `/category/${article.category}`,
                },
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 次阅读
                </span>
                {category && (
                  <Link
                    to={`/category/${article.category}`}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-600 rounded-full text-xs font-medium hover:bg-amber-100 transition-colors cursor-pointer"
                  >
                    <i className={`${category.icon}`}></i>
                    {category.name}
                  </Link>
                )}
              </div>
            </header>

            <figure className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-64 md:h-96 object-cover"
                title={article.title}
              />
            </figure>

            <div
              className="prose prose-lg max-w-none mb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 pt-6 border-t border-gray-100">
              <span className="text-sm text-gray-500 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-gray-50/80 py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((related: Article) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group block bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-amber-200 transition-all duration-300 cursor-pointer hover:-translate-y-1"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={related.title}
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-amber-600 transition-colors">
                        {related.title}
                      </h3>
                      <p className="text-xs text-gray-400 mt-2">
                        {related.date}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}